// Which rows in the schedules table get an Orders button.
//
// `file_classification_cache` is a PER-SCHEMA table but a storage path is not —
// the owning schema is encoded in the path (`New/Reports/{schema}/...`). The
// first version of this read the cache with one unqualified `supabase.from()`,
// which uses the VIEWER's active schema. A clinician looking at a patient whose
// files live in another schema (cross-schema providers, fax group fan-out) got
// zero rows and therefore no button — while the View Reports modal, which
// already resolves the schema per path, listed the very same orders.
import { PUBLIC_SCHEMA } from "./reportsPathSchema";

/**
 * Schema owning a reports FOLDER — `New/Reports/{schema}/{Name}_{ehr}`.
 *
 * `schemaFromReportsPath` answers the same question for a FILE, and requires
 * five segments to do it. A folder has four, so it would call every one of
 * these `public` and we would query the wrong schema for all of them. Same
 * rules otherwise: the third segment is the schema unless it looks like a
 * patient folder (a comma means "Last, First") or the fax staging area.
 */
export function schemaFromReportsFolder(folder) {
  const parts = String(folder || "").replace(/\/+$/, "").split("/");
  if (parts.length >= 4 && parts[0] === "New" && parts[1] === "Reports") {
    const candidate = parts[2];
    if (candidate && !candidate.includes(",") && !candidate.startsWith("Pending")) {
      return candidate;
    }
  }
  return PUBLIC_SCHEMA;
}

/** [{ id, folder }] for every visible row that has a reports folder. */
export function scheduleFolders(schedules, folderColumn, buildFolder) {
  const out = [];
  for (const schedule of schedules || []) {
    const raw = schedule?.[folderColumn] || buildFolder?.(schedule);
    const folder = String(raw || "").replace(/\/+$/, "");
    if (folder) out.push({ id: schedule.id, folder });
  }
  return out;
}

/** The distinct schemas those folders live in — one query each. */
export function schemasForFolders(folders) {
  return [...new Set((folders || []).map((f) => schemaFromReportsFolder(f.folder)))];
}

/**
 * { [scheduleId]: folder } for rows owning at least one order.
 *
 * Matched on `folder + "/"` rather than a bare prefix so a neighbouring folder
 * whose name merely starts the same ("…_456740" against "…_45674") does not
 * light up the wrong row.
 */
export function matchOrdersToSchedules(folders, orderPaths) {
  const paths = (orderPaths || []).filter(Boolean);
  const out = {};
  for (const { id, folder } of folders || []) {
    const prefix = `${folder}/`;
    if (paths.some((p) => p.startsWith(prefix))) out[id] = folder;
  }
  return out;
}
